/**
 * The World's reminders (RFC 0350 S3), as the directory lists them: id,
 * state and next due instant. A row opens its own schedule document for the
 * text, so the list costs one World read however many reminders there are.
 *
 * Reactive: an agent's `create_reminder` or a clock advancing a schedule
 * rewrites the World's directory, so the list refreshes on its own.
 */

import type { AnyDocumentId } from '@automerge/automerge-repo/slim'
import { useRepo } from '@automerge/automerge-repo-react-hooks'
import {
  projectScheduleDirectory,
  type ScheduleDirectoryRef,
  type ScheduleState,
  setScheduleState,
} from '@mod/core/model/schedules'
import { useCallback, useMemo } from 'react'
import { useLocalPersonId } from '../identity/localPerson'
import { useDocument } from './repo/useDocument'
import { readWorldPointer } from './world'

export function useWorldReminders(): {
  reminders: ScheduleDirectoryRef[]
  setState: (scheduleId: string, state: ScheduleState) => void
} {
  const repo = useRepo()
  const person = useLocalPersonId()
  const worldId = readWorldPointer() ?? undefined
  const [worldDoc] = useDocument(worldId as AnyDocumentId | undefined)
  const reminders = useMemo(
    () =>
      projectScheduleDirectory(worldDoc).filter((r) => r.state !== 'cancelled'),
    [worldDoc],
  )
  const setState = useCallback(
    (scheduleId: string, state: ScheduleState) => {
      if (!worldId) return
      void setScheduleState(
        repo,
        worldId,
        scheduleId,
        state,
        person ?? 'window',
      )
    },
    [repo, worldId, person],
  )
  return { reminders, setState }
}
